import {
    CONSENT_CHANGED_EVENT,
    CookieConsent,
    hasPersonalizationConsent,
    hasStoredCookieConsent,
    readCookieConsent,
    setCultureCookie,
    setCookieConsent
} from './consent';

export function initNav(): void {
    const burger = document.querySelector<HTMLButtonElement>('[data-nav-toggle]');
    const mobileNav = document.querySelector<HTMLElement>('[data-mobile-nav]');
    const desktopNavQuery = window.matchMedia('(min-width: 1101px)');
    let mobileNavScrollY = 0;
    let mobileNavOpen = false;
    let previousDocumentOverflow = '';
    let previousBodyOverflow = '';

    function lockPageScroll(): void {
        if (document.body.classList.contains('is-mobile-nav-open')) return;

        mobileNavScrollY = window.scrollY;
        previousDocumentOverflow = document.documentElement.style.overflow;
        previousBodyOverflow = document.body.style.overflow;

        document.documentElement.classList.add('is-mobile-nav-open');
        document.body.classList.add('is-mobile-nav-open');
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
    }

    function unlockPageScroll(): void {
        if (!document.body.classList.contains('is-mobile-nav-open')) return;

        document.documentElement.classList.remove('is-mobile-nav-open');
        document.body.classList.remove('is-mobile-nav-open');
        document.documentElement.style.overflow = previousDocumentOverflow;
        document.body.style.overflow = previousBodyOverflow;
        window.scrollTo(0, mobileNavScrollY);
    }

    function closeMobileNav(): void {
        if (!burger || !mobileNav || !mobileNavOpen) return;

        mobileNavOpen = false;
        mobileNav.classList.remove('is-open');
        burger.classList.remove('is-open');
        burger.setAttribute('aria-expanded', 'false');
        unlockPageScroll();

        window.setTimeout(() => {
            if (!mobileNavOpen) {
                mobileNav.hidden = true;
            }
        }, 340);
    }

    function openMobileNav(): void {
        if (!burger || !mobileNav || mobileNavOpen) return;

        mobileNavOpen = true;
        mobileNav.hidden = false;
        burger.classList.add('is-open');
        burger.setAttribute('aria-expanded', 'true');
        lockPageScroll();

        window.requestAnimationFrame(() => {
            mobileNav.classList.add('is-open');
        });
    }

    if (burger && mobileNav) {
        burger.addEventListener('click', () => {
            if (mobileNavOpen) {
                closeMobileNav();
                return;
            }

            openMobileNav();
        });

        mobileNav.querySelectorAll<HTMLAnchorElement>('[data-mobile-nav-link]').forEach(link => {
            link.addEventListener('click', closeMobileNav);
        });

        document.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closeMobileNav();
            }
        });

        desktopNavQuery.addEventListener('change', event => {
            if (event.matches) {
                closeMobileNav();
            }
        });
    }

    const accordionHideTimers = new WeakMap<HTMLElement, number>();
    const accordions = document.querySelectorAll<HTMLElement>('.mobile-nav__accordion');

    function setAccordionState(accordion: HTMLElement, isOpen: boolean): void {
        const trigger = accordion.querySelector<HTMLButtonElement>('[data-mobile-accordion-trigger]');
        const panel = accordion.querySelector<HTMLElement>('[data-mobile-accordion-panel]');
        if (!trigger || !panel) return;

        const pendingTimer = accordionHideTimers.get(accordion);
        if (pendingTimer) {
            window.clearTimeout(pendingTimer);
            accordionHideTimers.delete(accordion);
        }

        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

        if (isOpen) {
            panel.hidden = false;
            window.requestAnimationFrame(() => accordion.classList.add('is-open'));
            return;
        }

        accordion.classList.remove('is-open');
        const timer = window.setTimeout(() => {
            if (!accordion.classList.contains('is-open')) {
                panel.hidden = true;
            }
            accordionHideTimers.delete(accordion);
        }, 330);
        accordionHideTimers.set(accordion, timer);
    }

    accordions.forEach(accordion => {
        const trigger = accordion.querySelector<HTMLButtonElement>('[data-mobile-accordion-trigger]');
        if (!trigger) return;

        trigger.addEventListener('click', () => {
            const shouldOpen = trigger.getAttribute('aria-expanded') !== 'true';

            accordions.forEach(otherAccordion => {
                setAccordionState(otherAccordion, otherAccordion === accordion && shouldOpen);
            });
        });
    });

    // Scroll state for header
    const header = document.querySelector('.site-header');
    if (header) {
        const onScroll = () => header.classList.toggle('header--scrolled', window.scrollY > 20);
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
    }

    // Mega menu - safe diagonal-movement hover with mouseenter/mouseleave
    const hasMegas = document.querySelectorAll<HTMLElement>('.has-mega');
    let closeTimer = 0;

    function openMenu(menu: HTMLElement): void {
        if (!desktopNavQuery.matches) return;
        clearTimeout(closeTimer);
        document.querySelectorAll<HTMLElement>('.has-mega').forEach(p => p.classList.remove('is-open'));
        document.querySelectorAll<HTMLElement>('[data-menu-panel]').forEach(m => {
            if (m !== menu) m.style.display = 'none';
        });
        menu.style.display = 'block';
        menu.closest<HTMLElement>('.has-mega')?.classList.add('is-open');
    }

    function scheduleClose(): void {
        if (!desktopNavQuery.matches) return;
        closeTimer = window.setTimeout(() => {
            document.querySelectorAll<HTMLElement>('[data-menu-panel]').forEach(m => {
                m.style.display = 'none';
            });
            document.querySelectorAll<HTMLElement>('.has-mega').forEach(p => p.classList.remove('is-open'));
        }, 250);
    }

    hasMegas.forEach(parent => {
        const trigger = parent.querySelector<HTMLElement>('[data-menu-trigger]');
        if (!trigger) return;
        const menuId = `mega-${trigger.getAttribute('data-menu-trigger')}`;
        const menu = document.getElementById(menuId);
        if (!menu) return;

        parent.addEventListener('mouseenter', () => openMenu(menu));
        parent.addEventListener('mouseleave', scheduleClose);
        menu.addEventListener('mouseenter', () => clearTimeout(closeTimer));
        menu.addEventListener('mouseleave', scheduleClose);
    });

    // Close on click/focus outside
    document.addEventListener('click', (e: MouseEvent) => {
        if (!desktopNavQuery.matches) return;
        if (!(e.target as Element).closest('.has-mega') && !(e.target as Element).closest('.mega-menu')) {
            clearTimeout(closeTimer);
            document.querySelectorAll<HTMLElement>('[data-menu-panel]').forEach(m => m.style.display = 'none');
            document.querySelectorAll<HTMLElement>('.has-mega').forEach(p => p.classList.remove('is-open'));
        }
    });

    desktopNavQuery.addEventListener('change', event => {
        if (!event.matches) {
            clearTimeout(closeTimer);
            document.querySelectorAll<HTMLElement>('[data-menu-panel]').forEach(m => m.style.display = 'none');
            document.querySelectorAll<HTMLElement>('.has-mega').forEach(p => p.classList.remove('is-open'));
        }
    });
}

function syncCultureCookieConsentInputs(): void {
    const value = hasPersonalizationConsent() ? 'true' : 'false';
    document.querySelectorAll<HTMLAnchorElement>('[data-culture-link]').forEach(anchor => {
        const lang = anchor.getAttribute('data-lang');
        const returnUrl = anchor.getAttribute('data-return-url');
        if (!lang || !returnUrl) return;

        const url = new URL(anchor.href, window.location.origin);
        url.searchParams.delete('returnurl');
        url.searchParams.delete('returnUrl');
        url.searchParams.delete('usecookie');
        url.searchParams.delete('useCookie');
        url.searchParams.set('lang', lang);
        url.searchParams.set('returnUrl', returnUrl);
        url.searchParams.set('useCookie', value);
        anchor.href = `${url.pathname}${url.search}${url.hash}`;
    });
}

function getCurrentCulture(): string {
    return (document.body.getAttribute('data-culture') || document.documentElement.lang || 'en').toLowerCase();
}

function getDefaultCulture(): string {
    return (document.body.getAttribute('data-default-culture') || 'en').toLowerCase();
}

function shouldPersistCultureInUrl(): boolean {
    if (hasPersonalizationConsent()) return false;

    const url = new URL(window.location.href);
    return (getCurrentCulture() !== getDefaultCulture())
        || url.searchParams.has('lang');
}

function applyCultureToUrl(url: URL, culture: string): void {
    url.searchParams.set('lang', culture);
}

function removeCultureFromUrl(url: URL): void {
    url.searchParams.delete('lang');
}

function isCultureEndpoint(url: URL): boolean {
    return url.pathname.toLowerCase() === '/misc/setculture';
}

export function initAssistant(): void {
    const toggles = document.querySelectorAll('[data-assistant-toggle]');
    const assistantWindow = document.querySelector<HTMLElement>('[data-assistant-window]');
    if (assistantWindow) {
        const setOpen = (open: boolean): void => {
            assistantWindow.hidden = !open;
            toggles.forEach(btn => btn.setAttribute('aria-expanded', open ? 'true' : 'false'));
        };

        toggles.forEach(btn => btn.addEventListener('click', () => setOpen(assistantWindow.hidden)));
        setOpen(false);
    }
}

export function initLanguageDropdown(): void {
    const toggle = document.querySelector('[data-lang-toggle]');
    const dropdown = document.querySelector<HTMLElement>('[data-lang-dropdown]');
    syncCultureCookieConsentInputs();

    if (toggle && dropdown) {
        toggle.addEventListener('click', (e) => {
            e.stopPropagation();
            dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
        });
        document.addEventListener('click', () => dropdown.style.display = 'none');
    }

    document.querySelectorAll<HTMLAnchorElement>('[data-culture-link]').forEach(anchor => {
        anchor.addEventListener('click', () => {
            const targetLanguage = anchor.dataset.lang || '';
            if (!targetLanguage) return;

            window.japonicsAnalytics?.track('language_change', {
                from_language: getCurrentCulture(),
                to_language: targetLanguage,
                persisted: hasPersonalizationConsent()
            });
        });
    });
}

export function initCookieBanner(): void {
    const banner = document.querySelector<HTMLElement>('[data-cookie-banner]');
    const dialog = document.querySelector<HTMLDialogElement>('[data-cookie-dialog]');
    const dialogClose = dialog?.querySelector<HTMLButtonElement>('[data-cookie-close]');
    const rejectButtons = document.querySelectorAll<HTMLButtonElement>('[data-cookie-reject]');
    const saveBtn = dialog?.querySelector<HTMLButtonElement>('[data-cookie-save]');
    const preferenceButtons = document.querySelectorAll<HTMLElement>('[data-cookie-preferences]');

    const syncBannerState = (): void => {
        const isVisible = Boolean(banner && !banner.hidden);
        document.body.classList.toggle('has-cookie-banner', isVisible);
        document.querySelector<HTMLElement>('[data-assistant]')?.classList.toggle('assistant--suppressed', isVisible);
    };

    const setDialogState = (consent: CookieConsent | null): void => {
        const analytics = dialog?.querySelector<HTMLInputElement>('[data-cookie-toggle="analytics"]');
        const personalization = dialog?.querySelector<HTMLInputElement>('[data-cookie-toggle="personalization"]');
        const marketing = dialog?.querySelector<HTMLInputElement>('[data-cookie-toggle="marketing"]');
        if (analytics) analytics.checked = consent?.analytics === true;
        if (personalization) personalization.checked = consent?.personalization === true;
        if (marketing) marketing.checked = consent?.marketing === true;
    };

    const closeBanner = (): void => {
        if (banner) banner.hidden = true;
        syncBannerState();
    };

    const showBanner = (): void => {
        if (banner) banner.hidden = false;
        syncBannerState();
    };

    const openDialog = (event?: Event): void => {
        event?.preventDefault();
        setDialogState(readCookieConsent());
        dialog?.showModal?.();
    };

    preferenceButtons.forEach(button => {
        button.addEventListener('click', openDialog);
    });

    dialogClose?.addEventListener('click', () => dialog?.close());

    rejectButtons.forEach(rejectBtn => rejectBtn.addEventListener('click', () => {
        setCookieConsent({ analytics: false, personalization: false, marketing: false }, 'reject_optional');
        syncCultureCookieConsentInputs();
        closeBanner();
        dialog?.close();
    }));

    saveBtn?.addEventListener('click', () => {
        const analytics = dialog?.querySelector<HTMLInputElement>('[data-cookie-toggle="analytics"]')?.checked ?? false;
        const personalization = dialog?.querySelector<HTMLInputElement>('[data-cookie-toggle="personalization"]')?.checked ?? false;
        const marketing = dialog?.querySelector<HTMLInputElement>('[data-cookie-toggle="marketing"]')?.checked ?? false;
        setCookieConsent({ analytics, personalization, marketing }, 'save_preferences');
        syncCultureCookieConsentInputs();
        closeBanner();
        dialog?.close();
    });

    window.addEventListener(CONSENT_CHANGED_EVENT, () => {
        if (hasPersonalizationConsent()) {
            setCultureCookie(getCurrentCulture());
        }
        syncCultureCookieConsentInputs();
    });

    if (hasPersonalizationConsent()) {
        setCultureCookie(getCurrentCulture());
    }

    if (!banner) return;

    const acceptBtn = banner.querySelector<HTMLButtonElement>('[data-cookie-accept]');

    acceptBtn?.addEventListener('click', () => {
        setCookieConsent({ analytics: true, personalization: true, marketing: true }, 'accept_all');
        syncCultureCookieConsentInputs();
        closeBanner();
    });

    if (hasStoredCookieConsent()) return;

    setTimeout(showBanner, 800);
}

export function initCulturePersistence(): void {
    const culture = getCurrentCulture();
    const defaultCulture = getDefaultCulture();

    document.addEventListener('click', (event: MouseEvent) => {
        const anchor = (event.target as Element | null)?.closest<HTMLAnchorElement>('a[href]');
        if (!anchor) return;

        const href = anchor.getAttribute('href');
        if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) {
            return;
        }

        const url = new URL(anchor.href, window.location.origin);
        if (url.origin !== window.location.origin) return;
        if (anchor.matches('[data-culture-link]') || anchor.matches('[data-localized]') || isCultureEndpoint(url)) return;

        if (shouldPersistCultureInUrl()) {
            applyCultureToUrl(url, culture);
        } else if (culture === defaultCulture) {
            removeCultureFromUrl(url);
        }

        anchor.href = `${url.pathname}${url.search}${url.hash}`;
    });

    document.addEventListener('submit', (event: SubmitEvent) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;

        const method = (form.method || 'get').toLowerCase();
        if (method !== 'get') return;

        let cultureInput = form.querySelector<HTMLInputElement>('input[name="lang"][data-auto-culture]');

        if (shouldPersistCultureInUrl()) {
            if (!cultureInput) {
                cultureInput = document.createElement('input');
                cultureInput.type = 'hidden';
                cultureInput.name = 'lang';
                cultureInput.setAttribute('data-auto-culture', 'true');
                form.appendChild(cultureInput);
            }

            cultureInput.value = culture;
            return;
        }

        cultureInput?.remove();
    });
}

export function initResourceFilter(): void {
    const filterGroups = document.querySelectorAll<HTMLElement>('[data-filter-group]');
    if (!filterGroups.length) return;

    const setFilterSelectValue = (select: HTMLSelectElement, value: string): void => {
        select.value = value;
        select.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const normalizeFilterText = (value: string | null | undefined): string =>
        (value ?? '').trim().toLowerCase();

    filterGroups.forEach(group => {
        const groupName = group.getAttribute('data-filter-group')!;

        group.querySelectorAll<HTMLInputElement>('[data-filter-search]').forEach(input => {
            input.addEventListener('input', applyFilters);
        });

        group.querySelectorAll<HTMLButtonElement>('[data-filter]').forEach(btn => {
            btn.addEventListener('click', () => {
                group.querySelectorAll('[data-filter]').forEach(b => b.classList.remove('is-active'));
                document.querySelectorAll<HTMLElement>(`[data-filter-jump][data-filter-group-target="${groupName}"]`).forEach(b => b.classList.remove('is-active'));
                btn.classList.add('is-active');
                const select = group.querySelector<HTMLSelectElement>('[data-filter-select]');
                if (select) setFilterSelectValue(select, btn.getAttribute('data-filter') ?? 'all');
                window.japonicsAnalytics?.track('filter_apply', {
                    filter_group: groupName,
                    filter_value: btn.getAttribute('data-filter') ?? 'all'
                });
                applyFilters();
            });
        });

        group.querySelectorAll<HTMLSelectElement>('[data-filter-select]').forEach(select => {
            select.addEventListener('change', () => {
                const selected = select.value || 'all';
                group.querySelectorAll('[data-filter]').forEach(b => {
                    const isActive = (b.getAttribute('data-filter') ?? '').toLowerCase() === selected.toLowerCase();
                    b.classList.toggle('is-active', isActive);
                });
                document.querySelectorAll<HTMLElement>(`[data-filter-jump][data-filter-group-target="${groupName}"]`).forEach(b => b.classList.remove('is-active'));
                window.japonicsAnalytics?.track('filter_apply', {
                    filter_group: groupName,
                    filter_value: selected
                });
                applyFilters();
            });
        });
    });

    document.querySelectorAll<HTMLButtonElement>('[data-filter-jump]').forEach(btn => {
        if (btn.dataset.filterJumpBound === 'true') return;
        btn.dataset.filterJumpBound = 'true';

        btn.addEventListener('click', () => {
            const selected = (btn.getAttribute('data-filter-jump') ?? '').toLowerCase();
            const groupName = btn.getAttribute('data-filter-group-target') ?? 'crafts';
            const group = document.querySelector<HTMLElement>(`[data-filter-group="${groupName}"]`);
            const target = document.querySelector<HTMLElement>(`[data-filter-target="${groupName}"]`);
            if (!selected || !group || !target) return;

            const filterButton = Array.from(group.querySelectorAll<HTMLButtonElement>('[data-filter]'))
                .find(candidate => (candidate.getAttribute('data-filter') ?? '').toLowerCase() === selected);
            const select = group.querySelector<HTMLSelectElement>('[data-filter-select]');
            if (!filterButton && !select) return;

            group.querySelectorAll('[data-filter]').forEach(b => b.classList.remove('is-active'));
            filterButton?.classList.add('is-active');
            if (select) setFilterSelectValue(select, selected);
            document.querySelectorAll<HTMLElement>(`[data-filter-jump][data-filter-group-target="${groupName}"]`).forEach(b => b.classList.remove('is-active'));
            btn.classList.add('is-active');
            window.japonicsAnalytics?.track('filter_apply', {
                filter_group: groupName,
                filter_value: selected
            });
            applyFilters();

            (document.getElementById(btn.getAttribute('aria-controls') ?? '') ?? target)
                .scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    });

    function applyFilters(): void {
        const active: Record<string, string> = {};
        const activeSearch: Record<string, string> = {};

        filterGroups.forEach(g => {
            const groupName = g.getAttribute('data-filter-group')!;
            const activeBtn = g.querySelector<HTMLButtonElement>('[data-filter].is-active');
            const activeSelect = g.querySelector<HTMLSelectElement>('[data-filter-select]');
            const searchInput = g.querySelector<HTMLInputElement>('[data-filter-search]');

            activeSearch[groupName] = normalizeFilterText(searchInput?.value);

            if (activeSelect) {
                active[groupName] = activeSelect.value || 'all';
            } else if (activeBtn) {
                active[groupName] = activeBtn.getAttribute('data-filter') ?? 'all';
            }
        });

        document.querySelectorAll<HTMLElement>('[data-filter-target]').forEach(target => {
            const targetName = target.getAttribute('data-filter-target') ?? '';
            const selected = (active[targetName] ?? 'all').toLowerCase();
            const query = activeSearch[targetName] ?? '';
            const items = Array.from(target.querySelectorAll<HTMLElement>('[data-filter-item]'));
            if (!items.length) return;

            items.forEach(item => {
                const values = (item.getAttribute('data-filter-values') ?? '').toLowerCase().split(/\s+/);
                const searchText = normalizeFilterText(item.getAttribute('data-search-text') ?? item.textContent);
                const matchesSelect = selected === 'all' || values.includes(selected);
                const matchesQuery = !query || searchText.includes(query);

                item.hidden = !matchesSelect || !matchesQuery;
                if (!item.hidden && item.hasAttribute('data-reveal')) {
                    item.style.opacity = '1';
                    item.style.transform = 'none';
                }
            });

            let empty = target.querySelector<HTMLElement>('[data-filter-empty]');
            const visibleCount = items.filter(item => !item.hidden).length;
            if (!visibleCount) {
                if (!empty) {
                    empty = document.createElement('p');
                    empty.className = 'muted craft-empty';
                    empty.setAttribute('data-filter-empty', '');
                    empty.textContent = target.getAttribute('data-empty-text') ?? '';
                    target.appendChild(empty);
                }
                empty.hidden = false;
            } else if (empty) {
                empty.hidden = true;
            }
        });

        // Resources: filter by type + category data attributes
        const resourceList = document.querySelector<HTMLElement>('[data-resource-list]');
        if (resourceList) {
            resourceList.querySelectorAll<HTMLElement>('.content-card').forEach(card => {
                const typeMatch = !active['type'] || active['type'] === 'all' || card.dataset.type === active['type'];
                const category = card.dataset.category ?? card.dataset.technology;
                const catMatch  = !active['category'] || active['category'] === 'all' || category === active['category'];
                card.hidden = !(typeMatch && catMatch);
            });
        }
    }
}
